import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../contexts/ThemeContext";

interface ThemeToggleProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

// Generate confetti pieces with deterministic positions
const generateConfetti = (count: number) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: (i * 17) % 100, // Spread across screen
    delay: (i * 0.02) % 0.5,
    color: ["#22C55E", "#FFD700", "#EF4444", "#3B82F6", "#F59E0B", "#EC4899"][i % 6],
    size: 8 + (i % 3) * 4,
  }));
};

/**
 * Sporty theme toggle button
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  size = "md",
  className = "",
}) => {
  const { isDark, toggleTheme } = useTheme();

  const sizes = {
    sm: { width: 48, height: 24, ball: 18, icon: 12 },
    md: { width: 56, height: 28, ball: 22, icon: 14 },
    lg: { width: 64, height: 32, ball: 26, icon: 16 },
  };

  const { width, height, ball, icon } = sizes[size];
  const padding = (height - ball) / 2;

  return (
    <button
      onClick={toggleTheme}
      className={`relative group ${className}`}
      title={isDark ? "Switch to Day Game" : "Switch to Night Game"}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {/* Track */}
      <div
        className={`
          relative overflow-hidden rounded-full transition-all duration-300
          ${
            isDark
              ? "bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 shadow-inner"
              : "bg-gradient-to-r from-primary/20 via-primary/30 to-primary/20"
          }
        `}
        style={{ width, height }}
      >
        {/* Stadium light beams (for dark mode) */}
        {isDark && (
          <>
            <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-white/30 to-transparent transform -skew-x-12" />
            <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-white/20 to-transparent transform skew-x-12" />
          </>
        )}

        {/* Sun rays (for light mode) */}
        {!isDark && (
          <div className="absolute inset-0 opacity-50">
            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent transform -translate-y-1/2" />
          </div>
        )}

        {/* Stars (for dark mode) */}
        {isDark && (
          <div className="absolute inset-0">
            <div className="absolute top-1 left-2 w-0.5 h-0.5 bg-white rounded-full animate-pulse" />
            <div className="absolute top-2 right-3 w-0.5 h-0.5 bg-white/70 rounded-full" />
            <div
              className="absolute bottom-2 left-3 w-0.5 h-0.5 bg-white/50 rounded-full animate-pulse"
              style={{ animationDelay: "0.5s" }}
            />
          </div>
        )}

        {/* Ball / Toggle indicator */}
        <motion.div
          className={`
            absolute top-1/2 -translate-y-1/2 rounded-full
            flex items-center justify-center
            shadow-lg
            ${
              isDark
                ? "bg-gradient-to-br from-slate-200 to-slate-400"
                : "bg-gradient-to-br from-yellow-300 to-orange-400"
            }
          `}
          style={{ width: ball, height: ball }}
          initial={false}
          animate={{
            x: isDark ? width - ball - padding : padding,
            rotate: isDark ? 360 : 0,
          }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30,
          }}
        >
          {/* Moon / Sun icon inside ball */}
          <AnimatePresence mode="wait">
            {isDark ? (
              <motion.div
                key="moon"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                transition={{ duration: 0.2 }}
              >
                {/* Moon crescent */}
                <div
                  className="relative rounded-full bg-slate-300"
                  style={{ width: icon, height: icon }}
                >
                  <div
                    className="absolute top-0 -right-0.5 rounded-full bg-gradient-to-br from-slate-200 to-slate-400"
                    style={{ width: icon * 0.7, height: icon * 0.7 }}
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="sun"
                initial={{ scale: 0, rotate: 180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: -180 }}
                transition={{ duration: 0.2 }}
                className="relative flex items-center justify-center"
                style={{ width: icon, height: icon }}
              >
                {/* Sun rays */}
                {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                  <motion.div
                    key={angle}
                    className="absolute bg-gradient-to-t from-yellow-300 to-yellow-100"
                    style={{
                      width: icon * 0.12,
                      height: icon * 0.35,
                      borderRadius: icon * 0.1,
                      transformOrigin: "center bottom",
                      transform: `rotate(${angle}deg) translateY(-${icon * 0.55}px)`,
                    }}
                    animate={{
                      opacity: [0.6, 1, 0.6],
                      scaleY: [0.9, 1, 0.9],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: "easeInOut",
                    }}
                  />
                ))}
                {/* Sun core */}
                <div
                  className="rounded-full bg-gradient-to-br from-yellow-100 via-yellow-200 to-orange-200 relative z-10 shadow-inner"
                  style={{ width: icon * 0.6, height: icon * 0.6 }}
                >
                  {/* Inner highlight */}
                  <div
                    className="absolute top-0.5 left-0.5 rounded-full bg-white/60"
                    style={{ width: icon * 0.25, height: icon * 0.25 }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Label */}
      <span className="sr-only">
        {isDark ? "Switch to light mode" : "Switch to dark mode"}
      </span>
    </button>
  );
};

/**
 * Full-screen transition overlay - Clean, mobile-optimized sports celebration
 */
export const ThemeTransitionOverlay: React.FC = () => {
  const { isTransitioning, transitioningTo } = useTheme();

  // Use transitioningTo to determine which animation to show
  // This prevents the flash of wrong animation
  const showingDarkMode = transitioningTo === "dark";

  // Memoize confetti so positions don't change during animation
  // Use fewer particles on mobile for better performance
  const confetti = useMemo(() => generateConfetti(12), []);

  return (
    <AnimatePresence>
      {isTransitioning && transitioningTo && (
        <motion.div
          className="fixed inset-0 z-[99999] pointer-events-none overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Background - circular wipe from center */}
          <motion.div
            className={`absolute inset-0 ${
              showingDarkMode
                ? "bg-gradient-to-b from-slate-900 via-slate-950 to-black"
                : "bg-gradient-to-b from-sky-300 via-sky-400 to-sky-500"
            }`}
            initial={{ clipPath: "circle(0% at 50% 50%)" }}
            animate={{ clipPath: "circle(150% at 50% 50%)" }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          />

          {/* Stadium Lights */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <div
              className={`absolute top-0 left-[20%] w-24 md:w-32 h-[60vh] blur-3xl transform -rotate-12 ${
                showingDarkMode
                  ? "bg-gradient-to-b from-white/40 via-primary/20 to-transparent"
                  : "bg-gradient-to-b from-primary/30 via-yellow-300/20 to-transparent"
              }`}
            />
            <div
              className={`absolute top-0 right-[20%] w-24 md:w-32 h-[60vh] blur-3xl transform rotate-12 ${
                showingDarkMode
                  ? "bg-gradient-to-b from-white/40 via-primary/20 to-transparent"
                  : "bg-gradient-to-b from-primary/30 via-yellow-300/20 to-transparent"
              }`}
            />
          </motion.div>

          {/* Confetti Rain - works on mobile */}
          {confetti.map((piece) => (
            <motion.div
              key={piece.id}
              className="absolute rounded-sm"
              style={{
                left: `${piece.x}%`,
                width: piece.size,
                height: piece.size,
                backgroundColor: piece.color,
              }}
              initial={{ top: "-10%", rotate: 0, opacity: 0 }}
              animate={{
                top: "110%",
                rotate: 360 + (piece.id % 2 === 0 ? 180 : -180),
                opacity: [0, 1, 1, 0.8],
              }}
              transition={{
                duration: 1.0,
                delay: piece.delay,
                ease: "linear",
              }}
            />
          ))}

          {/* Center Icon - Trophy or Football - properly centered with flex */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: [0, 1.2, 1], rotate: 0 }}
              transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
            >
              {showingDarkMode ? (
                // Trophy for night mode - simplified animation for mobile performance
                <motion.div
                  className="relative flex items-center justify-center"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 0.8, repeat: 1, ease: "easeInOut" }}
                >
                  <svg
                    viewBox="0 0 48 64"
                    className="w-16 h-24 sm:w-20 sm:h-28 md:w-24 md:h-32"
                    style={{ filter: "drop-shadow(0 0 20px rgba(255,215,0,0.6))" }}
                  >
                    <defs>
                      <linearGradient id="trophyGold" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFD700" />
                        <stop offset="50%" stopColor="#FFA500" />
                        <stop offset="100%" stopColor="#DAA520" />
                      </linearGradient>
                    </defs>
                    <path d="M12 8 L36 8 L34 32 Q24 40 14 32 L12 8" fill="url(#trophyGold)" />
                    <path d="M8 8 Q4 8 4 16 Q4 24 12 24 L12 8 L8 8" fill="url(#trophyGold)" />
                    <path d="M40 8 Q44 8 44 16 Q44 24 36 24 L36 8 L40 8" fill="url(#trophyGold)" />
                    <rect x="20" y="36" width="8" height="12" fill="url(#trophyGold)" />
                    <rect x="14" y="48" width="20" height="8" rx="2" fill="url(#trophyGold)" />
                    <ellipse cx="24" cy="18" rx="5" ry="3" fill="rgba(255,255,255,0.4)" />
                  </svg>
                </motion.div>
              ) : (
                // Football for day mode - simplified animation for mobile performance
                <motion.div
                  className="relative flex items-center justify-center"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <svg
                    viewBox="0 0 64 40"
                    className="w-20 h-14 sm:w-24 sm:h-16 md:w-28 md:h-20"
                    style={{ filter: "drop-shadow(0 0 15px rgba(139,69,19,0.5))" }}
                  >
                    <defs>
                      <linearGradient id="footballBrown" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#8B4513" />
                        <stop offset="50%" stopColor="#A0522D" />
                        <stop offset="100%" stopColor="#654321" />
                      </linearGradient>
                    </defs>
                    {/* Football body - horizontal orientation */}
                    <ellipse cx="32" cy="20" rx="30" ry="18" fill="url(#footballBrown)" />
                    {/* White stripe/seam across center horizontally */}
                    <line x1="4" y1="20" x2="60" y2="20" stroke="white" strokeWidth="2" />
                    {/* Laces - vertical lines crossing the horizontal seam */}
                    <line x1="24" y1="14" x2="24" y2="26" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    <line x1="29" y1="13" x2="29" y2="27" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    <line x1="34" y1="13" x2="34" y2="27" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    <line x1="39" y1="14" x2="39" y2="26" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </motion.div>
              )}
            </motion.div>
          </div>

          {/* Sparkle bursts around center - reduced for mobile performance */}
          {[0, 90, 180, 270].map((angle, i) => (
            <motion.div
              key={angle}
              className="absolute top-1/2 left-1/2"
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
                x: Math.cos((angle * Math.PI) / 180) * 60,
                y: Math.sin((angle * Math.PI) / 180) * 60,
              }}
              transition={{
                duration: 0.5,
                delay: 0.3 + i * 0.08,
                ease: "easeOut",
              }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
                <path
                  fill={showingDarkMode ? "#FFD700" : "#22C55E"}
                  d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10L12 0Z"
                />
              </svg>
            </motion.div>
          ))}

          {/* Mode Label */}
          <motion.div
            className="absolute bottom-[20%] md:bottom-1/4 left-1/2 -translate-x-1/2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div
              className={`
                px-5 py-2.5 md:px-6 md:py-3 rounded-full font-bold text-base md:text-lg tracking-wide
                ${
                  showingDarkMode
                    ? "bg-slate-800/90 text-white border border-yellow-500/30"
                    : "bg-white/90 text-slate-800 border border-primary/30"
                }
                shadow-xl backdrop-blur-sm
              `}
            >
              {showingDarkMode ? "🏆 NIGHT GAME" : "🏈 DAY GAME"}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ThemeToggle;
